/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import React from "react";
import { Badge, Input } from "reactstrap";

import { useSignalSelection } from "../useSignalSelection";
import {
    CATEGORY_COLOR,
    CATEGORY_LABEL,
    getCellRelation,
    getPalletRelations,
    getRecommendedAction,
    getSignalCategory,
    Signal,
} from "../signalTypes";

const STATUS_DOT: Record<string, string> = {
    open: "#dc3545",
    resolved: "#198754",
    rejected: "#6c757d",
};

export const timeAgo = (iso: string): string => {
    const seconds = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
    return `${Math.floor(seconds / 86400)}d`;
};

interface SignalWorklistProps {
    signals: Signal[];
    activeSignalId?: string;
    onRowClick: (signal: Signal) => void;
    selection: ReturnType<typeof useSignalSelection>;
    selectable: boolean;
}

export const SignalWorklist: React.FC<SignalWorklistProps> = ({ signals, activeSignalId, onRowClick, selection, selectable }) => {
    if (signals.length === 0) {
        return <div className="text-muted p-3 text-center">No signals.</div>;
    }

    return (
        <div className="d-flex flex-column gap-2">
            {signals.map((signal) => {
                const category = getSignalCategory(signal);
                const cell = getCellRelation(signal);
                const pallets = getPalletRelations(signal);
                const action = getRecommendedAction(signal);
                // `!!activeSignalId &&` guards against `undefined === undefined`:
                // with no active signal, nothing may compare equal, however a
                // malformed signal's `id` happens to come out.
                const isActive = !!activeSignalId && signal.id === activeSignalId;

                return (
                    <div
                        key={signal.id}
                        role="button"
                        aria-current={isActive ? "true" : undefined}
                        // `bg-white` is DROPPED on the active row rather than overridden.
                        // Bootstrap's .bg-white is `!important` at single-class specificity,
                        // and so was the tint that used to override it — a tie broken only by
                        // which stylesheet is injected later. jsdom loads no Bootstrap, so a
                        // change in injection order would make D13's marker disappear in
                        // production with every test still green. Not applying the class at
                        // all removes the contest instead of winning it.
                        className={`${isActive ? "" : "bg-white"} rounded-2 border p-3 d-flex gap-3 align-items-start`}
                        css={css`
                            cursor: pointer;
                            border-left: 4px solid ${STATUS_DOT[signal.status] || "#adb5bd"} !important;
                            transition: box-shadow 0.15s ease-in-out;
                            box-shadow: ${isActive ? "0 2px 8px rgba(0,0,0,0.12)" : "none"};
                            /* D13: the shadow above is byte-identical to the hover shadow
                               below, so on its own it marks nothing — a hovered row and the
                               active row would be indistinguishable, and on touch (no hover)
                               it would be the only shadow ever seen. The background tint is
                               the actual marker; it is untouched by :hover and survives touch. */
                            ${isActive && css`background-color: #e7f3ff;`}
                            &:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.12); }
                        `}
                        onClick={() => onRowClick(signal)}
                    >
                        {selectable && (
                            <Input
                                type="checkbox"
                                className="mt-1"
                                aria-label={`Select ${signal.title}`}
                                checked={selection.isSelected(signal.id)}
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => selection.toggle(signal.id)}
                            />
                        )}

                        {/* min-width: 0 is what lets the title actually truncate. A flex item's
                            automatic minimum size is its content, so without this the long title
                            pushes this column — and the card with it — wider than the container,
                            and text-truncate never gets a width to truncate against. Needed on
                            the column AND on the title itself, since each is a flex item. */}
                        <div className="flex-grow-1" css={css`min-width: 0;`}>
                            <div className="d-flex align-items-center gap-2 mb-1">
                                {category && <Badge className="flex-shrink-0" color={CATEGORY_COLOR[category]}>{CATEGORY_LABEL[category]}</Badge>}
                                <span className="fw-bold text-truncate" css={css`min-width: 0;`}>{signal.title}</span>
                                <span className="ms-auto text-muted small flex-shrink-0">{timeAgo(signal.created)}</span>
                            </div>

                            <div className="small text-muted d-flex flex-wrap gap-2">
                                {cell && <span>{cell.facility_location_label || cell.facility_location_id}</span>}
                                {pallets.map((p) => (
                                    <span key={p.id}>· {p.layer_label || p.layer_id}</span>
                                ))}
                            </div>

                            {action && <div className="small mt-1"><em>{action}</em></div>}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default SignalWorklist;
